import { checkFirstResponseIfNeeded, ViewMode } from '@playbooks/action-cards-utils/src'
import { Assessment as AssessmentDataType, AssessmentType, Response } from '@playbooks/core/src/interfaces/ActionCard'
import { ArrowRight, ChevronDown, Minus, Plus } from 'lucide-react'
import { Badge } from '@playbooks/ui/components/badge'
import { Switch } from '@playbooks/ui/components/switch'
import { Input } from '@playbooks/ui/components/input'
import { cn } from '@playbooks/ui/lib/utils'

import { MultiSelect } from './components/MultiSelect'
import { SingleSelect } from './components/SingleSelect'
import useVars from '../../hooks/useVars'
import MarkdownText from '../../components/MarkdownText'
import { useAppContext } from '../../contexts/AppContext'
import { useState } from 'react'
import { useTranslation } from '../../contexts/localization/Localization'
import AssessmentHighlighter from './components/AssessmentHighlighter'

interface AssessmentProps {
  data: AssessmentDataType
}

export default function Assessment({ data }: AssessmentProps) {
  const {
    update,
    resetHighlights,
    highlightedIds,
    settings: { viewMode },
  } = useAppContext()
  const { text: t } = useTranslation()

  const isHighlighted = highlightedIds.some(value => value === data.id)
  const isFunnelView = viewMode === ViewMode.FUNNEL

  // Determine if "hide unselected responses" feature is configured for this assessment
  const isHideUnselectedFeatureSupported = !!data.isAutoCollapse && !!data.hideUnselected

  // Calculate the number of selected and unselected responses
  const selectedResponsesCount = data.responses.filter(response => response.checked).length
  const unselectedResponsesCount = data.responses.filter(response => !response.checked).length

  // "Show/hide unselected responses" toggle state.
  // If the feature is enabled, we hide the unselected responses by default
  // as long as something is selected or the assessment is collapsed (useful for Teammate)
  const [hideUnselectedResponses, setHideUnselectedResponses] = useState(
    isHideUnselectedFeatureSupported && (selectedResponsesCount > 0 || !data.checked),
  )

  const handleAssessmentEnabled = () => {
    if (!data.isAutoCollapse) return
    // We have to manually toggle the checked state here
    // because the checkFirstResponseIfNeeded needs the actual state
    // of the assessment data
    // Also this is a peculiarity of the action cards state updating (we mutate the data object directly)
    // eslint-disable-next-line react-hooks/immutability
    data.checked = !data.checked

    checkFirstResponseIfNeeded(data)
    updateHideUnselectedResponsesState(data.responses.filter(response => response.checked).length)
    update()
  }

  const showTreeText = !!(data.responses[data.responses.length - 1]?.checked && data.hasFreeText)

  const handleSingleSelectValuesChange = (response: Response) => {
    data.responses.forEach(option => (option.checked = false))
    updateHideUnselectedResponsesState(1)
    update(response, 'checked', !response.checked)
  }

  const handleMultiSelectValuesChange = (response: Response) => {
    const nextSelectedResponsesCount = response.checked ? selectedResponsesCount - 1 : selectedResponsesCount + 1
    updateHideUnselectedResponsesState(nextSelectedResponsesCount)
    update(response, 'checked', !response.checked)
  }

  const handleFreeTextChange = (value: string) => {
    update(data, 'freeTextValue', value)
  }

  const isSingleSelect = data.type === AssessmentType.SINGLE
  const SelectComponent = isSingleSelect ? SingleSelect : MultiSelect

  // Handle "hide unselected responses" logic
  const shouldHideUnselected = isHideUnselectedFeatureSupported && hideUnselectedResponses
  const shouldShowHiddenResponsesToggle =
    isHideUnselectedFeatureSupported && selectedResponsesCount > 0 && unselectedResponsesCount > 0

  const visibleResponses = shouldHideUnselected ? data.responses.filter(response => response.checked) : data.responses

  const handleValueChange = (response: Response) => {
    resetHighlights([data.id])
    if (isSingleSelect) {
      handleSingleSelectValuesChange(response)
    } else {
      handleMultiSelectValuesChange(response)
    }
  }
  const shouldRenderSelect = !data.isAutoCollapse || data.checked

  /** Update "Show/hide unselected responses" toggle state on selection change */
  function updateHideUnselectedResponsesState(selectedOptionsCount: number) {
    if (!isHideUnselectedFeatureSupported) return
    if (selectedOptionsCount === 0) {
      // No sense in hiding unselected responses when nothing is selected
      setHideUnselectedResponses(false)
    } else if (isSingleSelect) {
      // Once a response in a single-select assessment is selected,
      // we can hide other responses to avoid clutter
      setHideUnselectedResponses(true)
    }
  }

  /** Renders the "Show/hide unselected responses" toggle UI element with chevron */
  function renderHiddenResponsesToggle() {
    if (!shouldShowHiddenResponsesToggle) return null
    return (
      <button
        type="button"
        className={cn(
          'AC_show-hidden-responses mt-2 flex items-center gap-1 pl-3 text-xs text-muted-foreground hover:text-foreground',
          'transition-colors',
        )}
        onClick={() => setHideUnselectedResponses(prev => !prev)}
        aria-expanded={!hideUnselectedResponses}
      >
        {isFunnelView ? (
          hideUnselectedResponses ? (
            <Plus className="size-3.5" aria-hidden="true" />
          ) : (
            <Minus className="size-3.5" aria-hidden="true" />
          )
        ) : (
          <ChevronDown
            className={cn('size-3.5 transition-transform', !hideUnselectedResponses && 'rotate-180')}
            aria-hidden="true"
          />
        )}
        {hideUnselectedResponses
          ? t(unselectedResponsesCount === 1 ? 'assessmentShowHiddenOption' : 'assessmentShowHiddenOptions').replace(
              '{count}',
              unselectedResponsesCount.toString(),
            )
          : t('assessmentHideUnselectedOptions')}
      </button>
    )
  }

  return (
    <div
      className={cn(
        'relative AC_assessment w-full',
        data.type === AssessmentType.SINGLE ? 'AC_single' : 'AC_multi',
        data.checked && 'pb-1',
      )}
    >
      <div
        className={cn(
          'flex items-center mb-2 text-left',
          'group-data-[view=funnel]/app:gap-2',
          'group-data-[view=funnel]/app:flex-row-reverse',
          'group-data-[view=funnel]/app:justify-between',
        )}
        onClick={handleAssessmentEnabled}
      >
        {isHighlighted && <AssessmentHighlighter isFunnelView={isFunnelView} />}
        <div className="relative shrink-0 self-start -left-2 group-data-[view=funnel]/app:left-0">
          {data.isAutoCollapse ? (
            <Switch checked={data.checked} className="relative top-0.5 h-5 group-data-[view=funnel]/app:top-1.25" />
          ) : (
            <Badge className="group-data-[view=funnel]/app:hidden">
              <div className="px-0.75">
                <ArrowRight className="size-3" />
              </div>
            </Badge>
          )}
        </div>

        <h2
          className={cn(
            'text-foreground',
            'group-data-[view=funnel]/app:text-lg',
            'group-data-[view=funnel]/app:font-medium',
          )}
        >
          <Question assessment={data} />
        </h2>
      </div>

      {shouldRenderSelect && (
        <>
          <SelectComponent assessment={data} responses={visibleResponses} onValueChange={handleValueChange} />
          {showTreeText && (
            <div className="AC_free-text mt-4">
              <Input
                placeholder={t('freeTextPlaceholder', 'Type your answer here...')}
                value={data.freeTextValue ?? ''}
                onChange={e => handleFreeTextChange(e.target.value)}
              />
            </div>
          )}
          {renderHiddenResponsesToggle()}
        </>
      )}
    </div>
  )
}

function Question({ assessment }: { assessment: AssessmentDataType }) {
  const question = useVars(assessment.question)
  const { settings } = useAppContext()

  return (
    <span
      data-tags={JSON.stringify(assessment.tags?.map(t => t.id))}
      className={cn(assessment.isAutoCollapse && 'cursor-pointer')}
    >
      <MarkdownText text={question || ''} className={cn('')} />

      <span className="font-normal">{settings?.showMandatoryFields && assessment.required && question && '*'}</span>
    </span>
  )
}
